import React, { useEffect, useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { api } from '../lib/api'
import { asCollection } from '../lib/collections'
import { formatDateTimeMinute } from '../lib/date'
import { useToast } from '../ui/ToastProvider'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'
import { CATEGORY_OPTIONS_WITH_COMMON as CATEGORIES } from '../templates/categoryLanding/registry'
import CounselSurveyTemplateAdminPanel from './CounselSurveyTemplateAdminPanel'
import CounselSurveyQuestionBankPanel from './CounselSurveyQuestionBankPanel'
import MenuGuideButton from '../components/MenuGuideButton'

const FIELD_TYPES = [
  { value: 'text', label: '텍스트' },
  { value: 'textarea', label: '텍스트 영역' },
  { value: 'select', label: '선택 (드롭다운)' },
  { value: 'radio', label: '라디오' },
  { value: 'checkbox', label: '체크박스' },
  { value: 'number', label: '숫자' },
  { value: 'date', label: '날짜' },
  { value: 'tel', label: '전화번호' },
]

function parseJsonArray(raw, fallback = []) {
  if (!raw) return fallback
  if (Array.isArray(raw)) return raw
  try {
    const parsed = JSON.parse(String(raw))
    return Array.isArray(parsed) ? parsed : fallback
  } catch {
    return fallback
  }
}

function LandingSurveyTemplatesPanel() {
  const { show } = useToast()
  const [categoryFilter, setCategoryFilter] = useState('ALL')
  const [selectedId, setSelectedId] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('COMMON')
  const [active, setActive] = useState(true)
  const [fields, setFields] = useState([])

  const listQuery = useQuery({
    queryKey: ['survey-templates', categoryFilter],
    queryFn: async () => {
      const params = categoryFilter !== 'ALL' ? { category: categoryFilter } : {}
      const res = await api.get('/survey-templates', { params, __silent: true })
      return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
    },
  })

  const detailQuery = useQuery({
    queryKey: ['survey-template-detail', selectedId],
    enabled: !!selectedId,
    queryFn: async () => {
      const res = await api.get(`/survey-templates/${selectedId}`, { __silent: true })
      return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
    },
  })

  useEffect(() => {
    if (detailQuery.data) {
      const d = detailQuery.data
      setName(d.name || '')
      setDescription(d.description || '')
      setCategory(d.category || 'COMMON')
      setActive(d.active !== false)
      setFields(parseJsonArray(d.fields, []))
      setEditingId(d.id)
    } else if (!selectedId) {
      setName('')
      setDescription('')
      setCategory('COMMON')
      setActive(true)
      setFields([])
      setEditingId(null)
    }
  }, [detailQuery.data, selectedId])

  const createMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        category: category === 'COMMON' ? null : category,
        name,
        description,
        fields: JSON.stringify(fields),
        active,
      }
      const res = await api.post('/survey-templates', payload)
      return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
    },
    onSuccess: (data) => {
      show('템플릿이 생성되었습니다', 'success')
      setSelectedId(data?.id || null)
      listQuery.refetch()
    },
    onError: (e) => {
      show(e?.response?.data?.message || e?.message || '생성 실패', 'error')
    },
  })

  const updateMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name,
        description,
        fields: JSON.stringify(fields),
        active,
      }
      const res = await api.put(`/survey-templates/${editingId}`, payload)
      return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
    },
    onSuccess: () => {
      show('템플릿이 수정되었습니다', 'success')
      listQuery.refetch()
      detailQuery.refetch()
    },
    onError: (e) => {
      show(e?.response?.data?.message || e?.message || '수정 실패', 'error')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      await api.delete(`/survey-templates/${id}`)
    },
    onSuccess: () => {
      show('템플릿이 삭제되었습니다', 'success')
      setSelectedId(null)
      listQuery.refetch()
    },
    onError: (e) => {
      show(e?.response?.data?.message || e?.message || '삭제 실패', 'error')
    },
  })

  const toggleActiveMutation = useMutation({
    mutationFn: async (id) => {
      const res = await api.patch(`/survey-templates/${id}/active`)
      return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
    },
    onSuccess: () => {
      show('활성화 상태가 변경되었습니다', 'success')
      listQuery.refetch()
      if (selectedId) detailQuery.refetch()
    },
    onError: (e) => {
      show(e?.response?.data?.message || e?.message || '변경 실패', 'error')
    },
  })

  const addField = () => {
    setFields([...fields, { id: `q_${fields.length + 1}`, type: 'text', label: '', required: false, placeholder: '' }])
  }

  const updateField = (idx, key, value) => {
    const newFields = [...fields]
    newFields[idx] = { ...newFields[idx], [key]: value }
    setFields(newFields)
  }

  const removeField = (idx) => {
    if (window.confirm('이 필드를 삭제하시겠습니까?')) {
      setFields(fields.filter((_, i) => i !== idx))
    }
  }

  const moveField = (idx, direction) => {
    const newFields = [...fields]
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1
    if (targetIdx < 0 || targetIdx >= newFields.length) return
    const temp = newFields[idx]
    newFields[idx] = newFields[targetIdx]
    newFields[targetIdx] = temp
    setFields(newFields)
  }

  const copyField = (idx) => {
    const copied = JSON.parse(JSON.stringify(fields[idx]))
    copied.id = `${copied.id}_copy_${Date.now()}`
    const newFields = [...fields]
    newFields.splice(idx + 1, 0, copied)
    setFields(newFields)
  }

  const addOption = (idx) => {
    const newFields = [...fields]
    if (!newFields[idx].options) newFields[idx].options = []
    newFields[idx].options.push('')
    setFields(newFields)
  }

  const updateOption = (fieldIdx, optionIdx, value) => {
    const newFields = [...fields]
    newFields[fieldIdx].options[optionIdx] = value
    setFields(newFields)
  }

  const removeOption = (fieldIdx, optionIdx) => {
    const newFields = [...fields]
    newFields[fieldIdx].options = newFields[fieldIdx].options.filter((_, i) => i !== optionIdx)
    setFields(newFields)
  }

  const handleSave = () => {
    if (!name || name.trim() === '') {
      show('템플릿 이름을 입력해 주세요', 'error')
      return
    }
    if (fields.length === 0) {
      show('최소 1개 이상의 필드를 추가해 주세요', 'error')
      return
    }
    if (editingId) {
      updateMutation.mutate()
    } else {
      createMutation.mutate()
    }
  }

  const items = asCollection(listQuery.data)

  if (listQuery.isLoading) return <Loading />
  if (listQuery.isError) return <InlineError message="템플릿 목록을 불러오지 못했습니다." />

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">설문 템플릿 관리</h1>
          <MenuGuideButton slug="template-management" title="템플릿관리 가이드" />
        </div>
        <button
          type="button"
          className="rounded border px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-60"
          disabled={listQuery.isFetching}
          onClick={() => listQuery.refetch()}
        >
          {listQuery.isFetching ? '새로고침…' : '새로고침'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 목록 */}
        <div className="rounded-xl border bg-white p-4">
          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">카테고리 필터</label>
            <select
              className="w-full rounded border px-3 py-2 text-sm"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              {CATEGORIES.map((cat) => (
                <option key={cat.code} value={cat.code}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="text-center text-sm text-slate-500 py-8">템플릿이 없습니다</div>
            ) : (
              items.map((item) => (
                <div
                  key={item.id}
                  className={`rounded border p-3 cursor-pointer transition-colors ${
                    selectedId === item.id ? 'border-brand-navy bg-sky-50 ring-1 ring-brand-navy/10' : 'hover:bg-slate-50'
                  }`}
                  onClick={() => setSelectedId(item.id)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="font-semibold text-sm">{item.name || '-'}</div>
                      <div className="text-xs text-slate-500 mt-1">
                        {item.category ? CATEGORIES.find((c) => c.code === item.category)?.label || item.category : '공통'}
                      </div>
                      <div className="text-xs text-slate-400 mt-1">
                        {item.updatedAt ? formatDateTimeMinute(item.updatedAt) : '-'}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                          item.active ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {item.active ? '활성' : '비활성'}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* 편집 */}
        <div className="rounded-xl border bg-white p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-lg">{editingId ? '템플릿 수정' : '새 템플릿'}</h2>
            {editingId && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="rounded border border-teal-200 bg-teal-50 px-3 py-1.5 text-xs font-semibold text-teal-800 hover:bg-teal-100"
                  onClick={() => {
                    if (window.confirm('이 템플릿을 삭제하시겠습니까?')) {
                      deleteMutation.mutate(editingId)
                    }
                  }}
                  disabled={deleteMutation.isPending}
                >
                  삭제
                </button>
                <button
                  type="button"
                  className="rounded border px-3 py-1.5 text-xs hover:bg-slate-50"
                  onClick={() => {
                    toggleActiveMutation.mutate(editingId)
                  }}
                  disabled={toggleActiveMutation.isPending}
                >
                  {active ? '비활성화' : '활성화'}
                </button>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-semibold mb-1">템플릿 이름 *</label>
              <input
                className="w-full rounded border px-3 py-2 text-sm"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="예: 기본 설문"
                maxLength={200}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1">카테고리</label>
              <select
                className="w-full rounded border px-3 py-2 text-sm"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={!!editingId}
              >
                {CATEGORIES.filter((c) => c.code !== 'ALL').map((cat) => (
                  <option key={cat.code} value={cat.code}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1">설명</label>
              <textarea
                className="w-full rounded border px-3 py-2 text-sm min-h-[80px]"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="템플릿 설명"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-semibold">설문 필드 *</label>
                <button
                  type="button"
                  className="rounded border px-3 py-1.5 text-xs hover:bg-slate-50"
                  onClick={addField}
                >
                  + 필드 추가
                </button>
              </div>
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {fields.map((field, idx) => (
                  <div key={idx} className="rounded border p-3 bg-slate-50 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-slate-500 font-medium">필드 {idx + 1}</div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          className="rounded border px-2 py-1 text-xs hover:bg-white disabled:opacity-40"
                          disabled={idx === 0}
                          onClick={() => moveField(idx, 'up')}
                          title="위로 이동"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="rounded border px-2 py-1 text-xs hover:bg-white disabled:opacity-40"
                          disabled={idx === fields.length - 1}
                          onClick={() => moveField(idx, 'down')}
                          title="아래로 이동"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          className="rounded border px-2 py-1 text-xs hover:bg-white"
                          onClick={() => copyField(idx)}
                          title="복사"
                        >
                          복사
                        </button>
                        <button
                          type="button"
                          className="rounded border px-2 py-1 text-xs text-red-600 hover:bg-white"
                          onClick={() => removeField(idx)}
                          title="삭제"
                        >
                          삭제
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        className="rounded border px-2 py-1.5 text-sm bg-white"
                        value={field.type || 'text'}
                        onChange={(e) => updateField(idx, 'type', e.target.value)}
                      >
                        {FIELD_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                      <label className="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          checked={!!field.required}
                          onChange={(e) => updateField(idx, 'required', e.target.checked)}
                        />
                        필수
                      </label>
                    </div>
                    <input
                      className="w-full rounded border px-2 py-1.5 text-sm bg-white"
                      placeholder="필드 ID (예: q1)"
                      value={field.id || ''}
                      onChange={(e) => updateField(idx, 'id', e.target.value)}
                    />
                    <input
                      className="w-full rounded border px-2 py-1.5 text-sm bg-white"
                      placeholder="질문 라벨"
                      value={field.label || ''}
                      onChange={(e) => updateField(idx, 'label', e.target.value)}
                    />
                    {(field.type === 'text' || field.type === 'textarea' || field.type === 'tel') && (
                      <input
                        className="w-full rounded border px-2 py-1.5 text-sm bg-white"
                        placeholder="플레이스홀더"
                        value={field.placeholder || ''}
                        onChange={(e) => updateField(idx, 'placeholder', e.target.value)}
                      />
                    )}
                    {(field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && (
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <label className="text-xs text-slate-600">옵션</label>
                          <button
                            type="button"
                            className="rounded border border-teal-200 bg-teal-50 px-2 py-0.5 text-xs font-semibold text-teal-800 hover:bg-teal-100"
                            onClick={() => addOption(idx)}
                          >
                            + 옵션 추가
                          </button>
                        </div>
                        <div className="space-y-1">
                          {(field.options || []).map((opt, optIdx) => (
                            <div key={optIdx} className="flex items-center gap-1">
                              <input
                                className="flex-1 rounded border px-2 py-1 text-xs bg-white"
                                value={opt}
                                onChange={(e) => updateOption(idx, optIdx, e.target.value)}
                                placeholder="옵션 값"
                              />
                              <button
                                type="button"
                                className="rounded border px-2 py-1 text-xs text-red-600 hover:bg-white"
                                onClick={() => removeOption(idx, optIdx)}
                              >
                                삭제
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
                활성화
              </label>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <button
                type="button"
                className="rounded bg-brand-navy text-white px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-60"
                onClick={handleSave}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {editingId ? '수정' : '생성'}
              </button>
              {editingId && (
                <button
                  type="button"
                  className="rounded border px-4 py-2 text-sm hover:bg-slate-50"
                  onClick={() => {
                    setSelectedId(null)
                    setName('')
                    setDescription('')
                    setCategory('COMMON')
                    setActive(true)
                    setFields([])
                    setEditingId(null)
                  }}
                >
                  새로 만들기
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function SurveyTemplatesPage() {
  const [activeTab, setActiveTab] = useState('landing')

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">설문 템플릿 관리</h1>
          <MenuGuideButton slug="template-management" title="템플릿관리 가이드" />
        </div>
        <div className="mt-1 text-sm text-slate-500">랜딩설문과 고객설문을 분리해 운영 템플릿을 관리합니다.</div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setActiveTab('landing')}
          className={`rounded-full border px-4 py-2 text-sm font-semibold ${activeTab === 'landing' ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
        >
          랜딩설문
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('customer')}
          className={`rounded-full border px-4 py-2 text-sm font-semibold ${activeTab === 'customer' ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
        >
          고객설문
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('questions')}
          className={`rounded-full border px-4 py-2 text-sm font-semibold ${activeTab === 'questions' ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
        >
          문항관리
        </button>
      </div>

      {activeTab === 'landing' && <LandingSurveyTemplatesPanel />}
      {activeTab === 'customer' && <CounselSurveyTemplateAdminPanel />}
      {activeTab === 'questions' && <CounselSurveyQuestionBankPanel />}
    </div>
  )
}
